/*
  The signature scene.

  This is art direction, not something to derive from the project list — the
  arrangement is hand-composed, so it lives here as data. Each entry pairs a
  project with the slot it occupies. Slot 0 is the lead (the screen you dive
  into); clicking any other slot swaps it into the lead position.

  Geometry is expressed relative to the SCENE COLUMN, not the viewport, so the
  composition can never wander over the headline:

    left/top — position of the plate's centre within the scene column
    z        — depth in px; negative is further away
    ry/rx/rz — rotation in degrees
    s        — extra scale on top of the perspective foreshortening

  Reorder or re-tune freely. Only projects that have at least one screenshot
  are rendered, and any id that no longer exists is skipped.
*/

export type SceneDevice = 'desktop' | 'phone'

export interface SceneSlot {
  left: string
  top: string
  z: string
  ry: string
  rx: string
  rz: string
  s: string
}

export interface SceneItem {
  id: string
  device: SceneDevice
  /** Which screenshot to use, when the project has several. */
  shot?: number
}

/*
  The slots form a CLOSED RING, not a scatter — that is what makes the auto
  rotation read as an orbit instead of a shuffle. Walking the array in order
  traces one lap around an ellipse in the XZ plane:

      0 front-centre → 1 right → 2 right-back → 3 left-back → 4 left → (0)

  Slots 0 and 4 are neighbours on the ring, so a plate leaving the lead swings
  out to the left rather than teleporting across the stage. If you re-tune
  these, preserve that adjacency or the rotation will look like it skips.

  Keep the outer slots inside roughly 14%–86%: the section clips overflow, so
  anything beyond that loses an edge on narrower desktops. The vertical
  positions are deliberately uneven — a perfectly level ring looks mechanical.
*/
/*
  The orbit is INCLINED, like a carousel seen from slightly above: screen-Y
  falls as depth increases (back plates ride high, the lead sits low), the
  back pair pulls toward centre and shrinks — the ellipse foreshortening you
  would actually see. The plates themselves stay upright; only their path
  through space is tilted. If you retune, keep top% moving opposite to z or
  the inclination collapses.

  Right side stays ≤ ~79%: the section nav rail lives in the right gutter.
*/
export const sceneSlots: SceneSlot[] = [
  { left: '50%', top: '64%', z: '60px', ry: '-10deg', rx: '2deg', rz: '0deg', s: '1' },
  { left: '78%', top: '54%', z: '-150px', ry: '-22deg', rx: '3deg', rz: '1deg', s: '0.88' },
  { left: '86%', top: '36%', z: '-360px', ry: '-28deg', rx: '2deg', rz: '1deg', s: '0.76' },
  { left: '70%', top: '18%', z: '-550px', ry: '-16deg', rx: '1deg', rz: '0deg', s: '0.66' },
  { left: '50%', top: '12%', z: '-640px', ry: '0deg', rx: '0deg', rz: '0deg', s: '0.62' },
  { left: '30%', top: '18%', z: '-550px', ry: '16deg', rx: '1deg', rz: '0deg', s: '0.66' },
  { left: '14%', top: '36%', z: '-360px', ry: '28deg', rx: '2deg', rz: '-1deg', s: '0.76' },
  { left: '22%', top: '54%', z: '-150px', ry: '22deg', rx: '3deg', rz: '-1deg', s: '0.88' },
]

export const sceneItems: SceneItem[] = [
  { id: 'chinet-mobile', device: 'phone', shot: 0 },
  { id: 'raaz-tms', device: 'phone', shot: 0 },
  { id: 'hidyat-rider', device: 'phone', shot: 0 },
  { id: 'neoconnect-mobile', device: 'phone', shot: 0 },
  { id: 'expense-manager', device: 'phone', shot: 0 },
  { id: 'neo-pharmacy', device: 'desktop', shot: 0 },
  { id: 'neo-ems', device: 'desktop', shot: 0 },
  { id: 'newtab-extension', device: 'desktop', shot: 0 },
]
